import { memo, useCallback, useMemo } from "react";
import log from "logbeacon";
import { cn } from "utils";
import { ASKList } from "../TokenDetails/ASKList";
import { HomePageSender } from "../Home/HomePageSender";
import { IChatInfo } from "../ChatModal";
import { CopilotSenderTabInput } from "../CoPilot/CopilotAgent/CopilotSenderTabInput";
import type { SenderSubmitOptions } from "../Sender/type";
import { useIsPc, useRecommendQuestion } from "@/src/hooks";
import {
  buildDetailRecommendScopes,
  DETAIL_RECOMMEND_SCOPE_MAP,
} from "@/src/const";
import { RecommendQuestionRequest } from "@/src/services/recommendQuestionsService";
import { decodeString, encodeString } from "@/src/utils/stringEncoder";
import { useRedirectChat } from "@/src/hooks/useRedirectChat";
import { useRecommendQuestionTracking } from "@/src/hooks/useRecommendQuestionTracking";

/**
 * DetailSender 组件属性接口
 */
interface DetailSenderProps {
  /** 股票/代币符号 */
  symbol: string;
  /** 是否为股票（默认为 false） */
  isStock?: boolean;
  /** 自定义类名 */
  className?: string;
  source?: string;
  isPollToken?: boolean;
  // 股票id
  stockId?: string;
  // token id
  id?: string;
  questions?: { question?: string }[];
  containerClassName?: string;
}

/**
 * DetailSender 组件 - 股票详情页面的 AI 问答发送器
 *
 * 包含：
 * - 带有 logo 的标题区域
 * - ASK 组件用于 AI 问答交互
 */
const DetailSender = memo(
  ({
    symbol,
    stockId,
    id,
    source: propsSource,
    isStock = false,
    isPollToken,
    className,
    containerClassName,
    questions: propsQuestions = [],
  }: DetailSenderProps) => {
    const type = isStock ? "stock" : "crypto";
    const queryParams = buildDetailRecommendScopes(type, 20);
    const { questions } = useRecommendQuestion(
      {
        ...queryParams,
        related: isStock ? stockId : id,
      } as RecommendQuestionRequest,
      {
        enabled: !isPollToken,
        staleTime: 1000 * 60 * 5,
        key: DETAIL_RECOMMEND_SCOPE_MAP[type],
      },
    );
    const { handleCheckChatVaild, loading: sendLoading } = useRedirectChat();
    const [isPC] = useIsPc();

    const recommendQuestions = [...questions, ...propsQuestions];

    // 使用推荐问题跟踪 hook
    const {
      value,
      setValue,
      handleChange,
      handleQuestionSelect,
      shouldUseRecommendInfo,
      getRecommendInfo,
      reset: resetTracking,
    } = useRecommendQuestionTracking();
    const typeParams = isStock ? "stock" : "token";
    const source = propsSource || "detail";

    const initSign = useMemo(() => {
      const sign = symbol
        ? `${typeParams}_${encodeString(`${source}:symbol#${symbol}${stockId ? `+id#${stockId}` : ""}`)}`
        : "";
      return sign;
    }, [symbol, typeParams, source, stockId]);

    const handleSend = useCallback(
      (question: string | undefined, submitOptions?: SenderSubmitOptions) => {
        let sign = initSign;
        let requestParams = { question: question || "", sign } as IChatInfo;
        const isRecommend = shouldUseRecommendInfo(question);
        // 如果用户选中了推荐问题且没有修改输入，传递推荐问题信息
        if (isRecommend) {
          const selectedQuestion = getRecommendInfo();
          if (selectedQuestion) {
            log.info(
              `[onChartClick][trace]${isRecommend ? "[recommend]" : ""}[sender]`,
              requestParams,
            );
            const val = sign.split("_")[1] || "";
            sign = val
              ? `${typeParams}_${encodeString(`${decodeString(val)}+ref#${selectedQuestion.source_id}`)}`
              : `content_${encodeString(`chat:id#${selectedQuestion.source_id}`)}`;
            requestParams = {
              ...requestParams,
              sign,
              cacheQuestion: selectedQuestion.prompt || "",
            };
          }
        }
        handleCheckChatVaild({
          ...requestParams,
          senderContext: true,
          customParams: {
            attachments: submitOptions?.attachments,
            voiceContext: submitOptions?.voiceContext,
          },
        });
        // 清空状态
        resetTracking();
      },
      [
        initSign,
        shouldUseRecommendInfo,
        handleCheckChatVaild,
        resetTracking,
        getRecommendInfo,
        typeParams,
      ],
    );

    return isPC ? (
      <HomePageSender
        containerClassName={cn(
          "md:w-[calc(100%-32px)] w-full",
          containerClassName,
        )}
        onSend={handleSend}
        loading={sendLoading}
        value={value}
        onChange={handleChange}
        onQuestionSelect={handleQuestionSelect}
        data={recommendQuestions}
        className={className}
        senderQuestion={
          <div className="flex flex-row gap-2 pl-3 mt-3 md:mt-0 max-w-[calc(100vw-94px)] md:w-full overflow-x-auto no-scrollbar relative">
            <ASKList
              symbol={symbol}
              isPollToken={isPollToken}
              isStock={isStock}
              onChange={setValue}
              stockId={stockId}
            />
            {!isPC && (
              <div className="block md:hidden absolute -right-2 z-10 flex h-full w-5 items-center justify-end bg-gradient-to-l from-transparent via-[#252525]/100 to-transparent" />
            )}
          </div>
        }
      />
    ) : (
      <div className="fixed bottom-0 fixed-bottom-safe z-10 w-full">
        <CopilotSenderTabInput questions={questions} sign={initSign} />
      </div>
    );
  },
);

DetailSender.displayName = "DetailSender";

export { DetailSender };
export type { DetailSenderProps };
